import { ReactNode, forwardRef, cloneElement, isValidElement } from 'react';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { useIsTruncated } from '@/hooks/useIsTruncated';
import { cn } from '@/lib/utils';

interface TruncatedTooltipProps {
  /** The full text to show in the tooltip when truncated */
  text: string;
  /** The trigger element - should have truncate or line-clamp classes */
  children: ReactNode;
  /** Additional className for tooltip content */
  contentClassName?: string;
  /** Tooltip side */
  side?: 'top' | 'bottom' | 'left' | 'right';
  /** Delay before showing tooltip */
  delayDuration?: number;
  /** Dependency array to force re-check truncation */
  deps?: React.DependencyList;
}

/**
 * A smart tooltip that only shows when the text inside is actually truncated.
 * Works with both single-line (truncate) and multi-line (line-clamp-*) truncation.
 * 
 * Usage:
 * ```tsx
 * <TruncatedTooltip text={item.title}>
 *   <h3 className="truncate">{item.title}</h3>
 * </TruncatedTooltip>
 * ```
 */
export const TruncatedTooltip = forwardRef<HTMLElement, TruncatedTooltipProps>(
  function TruncatedTooltip(
    { 
      text, 
      children, 
      contentClassName,
      side = 'top',
      delayDuration = 0,
      deps = [],
    },
    _forwardedRef
  ) {
    const [truncateRef, isTruncated] = useIsTruncated<HTMLElement>(deps);

    // Clone the child element and attach our ref to it
    const childWithRef = isValidElement(children)
      ? cloneElement(children as React.ReactElement<{ ref?: React.Ref<HTMLElement> }>, {
          ref: truncateRef,
        })
      : children;

    return (
      <Tooltip delayDuration={delayDuration}>
        <TooltipTrigger asChild>
          {childWithRef}
        </TooltipTrigger>
        {isTruncated && (
          <TooltipContent 
            side={side}
            className={cn(
              'max-w-[280px] backdrop-blur-xl bg-black/60 border border-white/10',
              'shadow-[0_8px_32px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.1)]',
              'text-white/90',
              contentClassName
            )}
          >
            <p className="text-xs leading-relaxed break-words">{text}</p>
          </TooltipContent>
        )}
      </Tooltip>
    );
  }
);
